// Phone « More » sheet: every page that is not a bottom tab, grouped like the
// desktop menu, then the account (who is signed in, FR/EN, sign out — the old
// hamburger drawer's footer). A bottom sheet: slides up (240 ms), backdrop or
// Escape or a drag down on the grab handle closes it; reduced motion = no slide.
import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { useAuthStore } from '@/store/useAuthStore';
import { activeNavPath, moreGroups, ADMIN_NAV, type Access } from '@/lib/appShell/navModel';
import { NavIcon } from './navIcons';
import { ShellLink } from './ShellLink';
import { LangSwitcher } from './LangSwitcher';
import { AppearanceSwitch } from './AppearanceSwitch';

const EXIT_MS = 200;

export function MoreSheet({ open, onClose, access, pathname }: {
  open: boolean; onClose: () => void; access: Access; pathname: string;
}) {
  const { t } = useT();
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  // Stay mounted through the exit slide: mounted follows `open` up at once
  // (derived during render) and down after the slide (timer).
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);
  const [entered, setEntered] = useState(false);
  const shown = open && entered;
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<number | null>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    const tmr = setTimeout(() => { setMounted(false); setEntered(false); setDrag(0); }, EXIT_MS);
    return () => clearTimeout(tmr);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtn.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const back = opener.current;
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
      if (back instanceof HTMLElement) back.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  if (!mounted) return null;

  const active = activeNavPath(pathname, ADMIN_NAV);
  const groups = moreGroups(access);

  const onHandleStart = (e: TouchEvent) => { dragStart.current = e.touches[0].clientY; setDragging(true); };
  const onHandleMove = (e: TouchEvent) => {
    if (dragStart.current == null) return;
    setDrag(Math.max(0, e.touches[0].clientY - dragStart.current));
  };
  const onHandleEnd = () => {
    if (drag > 80) onClose(); else setDrag(0);
    dragStart.current = null;
    setDragging(false);
  };

  return (
    <div className="md:hidden fixed inset-0 z-50">
      <div
        data-sheet-backdrop
        aria-hidden
        onClick={onClose}
        className={cn('absolute inset-0 bg-black/45 transition-opacity duration-200 motion-reduce:transition-none', shown ? 'opacity-100' : 'opacity-0')}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('Plus')}
        className={cn(
          'absolute inset-x-0 bottom-0 flex flex-col bg-white rounded-t-2xl shadow-[0_-8px_32px_rgba(0,0,0,0.18)] dark:shadow-[0_-8px_32px_rgba(0,0,0,0.55)]',
          'max-h-[85dvh] motion-reduce:transition-none',
          !dragging && 'transition-transform duration-[240ms] ease-out',
        )}
        style={{
          transform: shown ? `translateY(${drag}px)` : 'translateY(100%)',
          paddingBottom: 'calc(var(--adm-safe-bottom) + 8px)',
        }}
      >
        <div
          className="shrink-0 pt-2 pb-1 flex items-center justify-between px-4 touch-none"
          onTouchStart={onHandleStart}
          onTouchMove={onHandleMove}
          onTouchEnd={onHandleEnd}
          onTouchCancel={onHandleEnd}
        >
          <span className="w-11" />
          <span aria-hidden className="h-1.5 w-10 rounded-full bg-gray-300" />
          <button
            ref={closeBtn}
            type="button"
            onClick={onClose}
            aria-label={t('Fermer')}
            className="w-11 h-11 -mr-2 grid place-items-center rounded-full text-gray-500 active:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A24A]/60"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-2">
          {groups.map((g) => (
            <section key={g.group} aria-labelledby={`more-g-${g.group}`} className="mb-3">
              <p id={`more-g-${g.group}`} data-nav-group-label className="px-3 pt-1 pb-1.5 text-[12px] font-semibold text-gray-400">
                {t(g.label)}
              </p>
              <ul className="rounded-xl overflow-hidden bg-adm-canvas dark:bg-adm-surface-2">
                {g.items.map((item, i) => {
                  const on = active === item.to;
                  return (
                    <li key={item.to} className={cn(i > 0 && 'border-t border-adm-hairline')}>
                      <ShellLink
                        to={item.to}
                        onGo={onClose}
                        data-nav-item
                        aria-current={on ? 'page' : undefined}
                        className={cn(
                          'flex items-center gap-3 px-3 min-h-[50px] text-[15px] transition-colors duration-150 active:bg-adm-gold-wash',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C9A24A]/60',
                          on ? 'text-adm-gold-ink font-semibold' : 'text-gray-800',
                        )}
                      >
                        <span aria-hidden className={on ? 'text-[#C9A24A]' : 'text-gray-400'}><NavIcon to={item.to} size={20} /></span>
                        <span className="flex-1 truncate">{t(item.label)}</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-gray-300" aria-hidden>
                          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </ShellLink>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          <section aria-label={t('Compte')} className="mt-4 mb-1 px-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[12px] text-gray-400">{t('Connecté en tant que')}</p>
                <p className="text-[14px] text-gray-800 font-medium truncate">{profile?.full_name ?? 'Admin'}</p>
              </div>
              <LangSwitcher />
            </div>
            {/* Night mode — every user, on their own device (lib/theme/adminTheme.ts). */}
            <div className="mt-4">
              <p className="pb-1.5 text-[12px] font-semibold text-gray-400">{t('Apparence')}</p>
              <AppearanceSwitch />
            </div>
            <button
              type="button"
              onClick={() => { onClose(); void signOut(); }}
              className="mt-4 w-full min-h-[48px] rounded-xl border border-red-200 text-red-700 text-[15px] font-medium active:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
            >
              {t('Déconnexion')}
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
